// @flow
import { applyMiddleware, compose, createStore, combineReducers } from 'redux';
import thunk from 'redux-thunk';
import { reducer as notifReducer } from 'redux-notifications';
import promisifyActionMiddleware from 'redux-promisify-action';
import Raven from 'raven-js';
import createRavenMiddleware from 'raven-for-redux';
import type { Reducer, StoreEnhancer } from 'redux';

import { isFetchingReducer, settingsReducer } from 'common/reducers';
import {
  reducer as routerReducer,
  middleware as routerMiddleware,
  enhancer as routerEnhancer,
} from 'modules/router';

import clearableReducer from './helpers/clearableReducer';
import sagaMiddleware from './saga';
import type { Action } from './types';

export const BOOTSTRAP = '@@BOOTSTRAP';
export const CLEAR = '@@CLEAR';

const composeEnhancers: * =
  // eslint-disable-next-line no-underscore-dangle
  typeof window === 'object' && typeof window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ === 'function'
    ? (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__: $Compose) // eslint-disable-line no-underscore-dangle
    : compose;

const reducers = {
  router: routerReducer,
  notifs: notifReducer,
  isFetching: isFetchingReducer,
  settings: settingsReducer,
};
let middleware = [promisifyActionMiddleware, thunk, sagaMiddleware, routerMiddleware];

// Middleware list
if (process.env.NODE_ENV === 'production') {
  middleware = [createRavenMiddleware(Raven), ...middleware];
} else {
  middleware = [...middleware];
}

// Create root reducer
function makeRootReducer<S: {}, O: Object, A: Action<any>>(nextReducers: O): Reducer<S, A> {
  const rootReducer: Reducer<S, A> = combineReducers(nextReducers);

  return clearableReducer([CLEAR, BOOTSTRAP], rootReducer);
}

const rootEnhancer: StoreEnhancer<any, any> = composeEnhancers(
  routerEnhancer,
  applyMiddleware(...middleware),
);

// Create main store
export const store = createStore(makeRootReducer(reducers), rootEnhancer);

export type Store = typeof store;

// Inject dynamically updated reducers into store
export function injectReducer<S, A>(key: string, reducer: Reducer<S, A>) {
  if (!Object.hasOwnProperty.call(reducers, key)) {
    reducers[key] = reducer;
    store.replaceReducer(makeRootReducer(reducers));
  }
}

export const bootstrapDispatch = () => store.dispatch({ type: BOOTSTRAP });

export default store;



// WEBPACK FOOTER //
// ./src/store/configureStore.js